import React, { useState, useEffect } from 'react'
import {
  Card, Row, Col, Button, Space, Statistic, Badge, Alert, Descriptions, Tag, Progress, App
} from 'antd'
import {
  ReloadOutlined, PlayCircleOutlined, StopOutlined, SettingOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, CloseCircleOutlined
} from '@ant-design/icons'
import { monitoringApi } from '@/services/api'
import dayjs from 'dayjs'

interface ProxyStatus {
  status: 'online' | 'offline'
  health?: any
  timestamp: number
  responseTime?: string
}

interface ProxyInfo {
  version?: string
  uptime?: number
  environment?: string
  config?: any
}

interface ProxyStats {
  totalRequests?: number
  successfulRequests?: number
  failedRequests?: number
  averageResponseTime?: number
  errorRate?: number
  // proxy.js 返回的数据结构
  requestStats?: {
    totalRequests?: number
    successfulRequests?: number
    failedRequests?: number
    tokenUsage?: {
      prompt?: number
      completion?: number
      total?: number
    }
  }
  apiStats?: any
  tokenUsage?: any
  sessionStart?: number
  lastUpdated?: number
  sessionDuration?: number
  apiLogStats?: any
}

const ProxyStatusPage: React.FC = () => {
  const { message } = App.useApp()
  const [status, setStatus] = useState<ProxyStatus | null>(null)
  const [info, setInfo] = useState<ProxyInfo | null>(null)
  const [stats, setStats] = useState<ProxyStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [testLoading, setTestLoading] = useState(false)
  const [restartLoading, setRestartLoading] = useState(false)

  const fetchData = async () => {
    try {
      setLoading(true)
      const [statusResponse, infoResponse, statsResponse] = await Promise.allSettled([
        monitoringApi.getProxyStatus(),
        monitoringApi.getProxyInfo(),
        monitoringApi.getProxyStats()
      ])

      if (statusResponse.status === 'fulfilled' && statusResponse.value.success) {
        setStatus(statusResponse.value.data)
      }

      if (infoResponse.status === 'fulfilled' && infoResponse.value.success) {
        setInfo(infoResponse.value.data)
      }

      if (statsResponse.status === 'fulfilled' && statsResponse.value.success) {
        setStats(statsResponse.value.data)
      }
    } catch (error) {
      console.error('获取Proxy状态失败:', error)
      message.error('获取Proxy状态失败')
    } finally {
      setLoading(false)
    }
  }

  const testConnection = async () => {
    try {
      setTestLoading(true)
      const response = await monitoringApi.testProxyConnection()
      
      if (response.success) {
        message.success(`连接测试成功，响应时间: ${response.data.responseTime}ms`)
      } else {
        message.error('连接测试失败')
      }
    } catch (error) {
      console.error('连接测试失败:', error)
      message.error('连接测试失败')
    } finally {
      setTestLoading(false)
    }
  }

  const restartProxy = async () => {
    try {
      setRestartLoading(true)
      const response = await monitoringApi.restartProxy()

      if (response.success) {
        message.success('Proxy服务重启成功')
        // 等待几秒后刷新状态
        setTimeout(fetchData, 3000)
      } else {
        // 🔥 修复：显示详细的错误信息和重启指导
        const errorMsg = response.error || 'Proxy服务重启失败'

        if (response.data?.instructions) {
          // 显示重启指导
          const instructions = response.data.instructions.join('\n')
          message.error({
            content: (
              <div>
                <div style={{ marginBottom: 8 }}>{errorMsg}</div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: 4 }}>重启指导：</div>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{instructions}</pre>
                </div>
              </div>
            ),
            duration: 10 // 显示10秒
          })
        } else {
          message.error(errorMsg)
        }

        console.error('重启Proxy服务失败:', response)
      }
    } catch (error) {
      console.error('重启Proxy服务失败:', error)
      message.error('重启Proxy服务失败: 网络连接错误')
    } finally {
      setRestartLoading(false)
    }
  }

  const syncData = async () => {
    try {
      const response = await monitoringApi.syncProxyData()
      if (response.success) {
        message.success('数据同步成功')
        fetchData()
      } else {
        message.error('数据同步失败')
      }
    } catch (error) {
      console.error('数据同步失败:', error)
      message.error('数据同步失败')
    }
  }

  useEffect(() => {
    fetchData()
    
    // 设置定时刷新
    const interval = setInterval(fetchData, 30000) // 30秒刷新一次
    
    return () => clearInterval(interval)
  }, [])

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'online':
        return <Badge status="success" text="在线" />
      case 'offline':
        return <Badge status="error" text="离线" />
      default:
        return <Badge status="default" text="未知" />
    }
  }

  const getStatusIcon = (status?: string) => {
    switch (status) {
      case 'online':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'offline':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
      default:
        return <ExclamationCircleOutlined style={{ color: '#faad14' }} />
    }
  }

  const formatUptime = (uptime?: number | string) => {
    if (!uptime) return '未知'

    // 如果是字符串，直接返回
    if (typeof uptime === 'string') return uptime

    const days = Math.floor(uptime / (24 * 60 * 60))
    const hours = Math.floor((uptime % (24 * 60 * 60)) / (60 * 60))
    const minutes = Math.floor((uptime % (60 * 60)) / 60)

    return `${days}天 ${hours}小时 ${minutes}分钟`
  }

  const getSuccessRate = () => {
    // 🔥 修复：处理 proxy.js 返回的数据结构
    let totalRequests = 0
    let successfulRequests = 0

    // 优先从 requestStats 获取数据
    if (stats?.requestStats) {
      totalRequests = stats.requestStats.totalRequests || 0
      successfulRequests = stats.requestStats.successfulRequests || 0
    } else {
      // 备用方案：从顶级字段获取
      totalRequests = stats?.totalRequests || 0
      successfulRequests = stats?.successfulRequests || 0
    }

    console.log('🔍 成功率计算数据:', { totalRequests, successfulRequests, stats })

    if (!totalRequests || totalRequests === 0) return 0
    const rate = (successfulRequests / totalRequests) * 100
    return Math.round(rate * 100) / 100 // 保留两位小数
  }

  return (
    <div style={{ padding: '0' }}>
      <Card title="Proxy状态监控" extra={
        <Space>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchData}
            loading={loading}
          >
            刷新
          </Button>
          <Button 
            icon={<PlayCircleOutlined />} 
            onClick={testConnection}
            loading={testLoading}
          >
            测试连接
          </Button>
          <Button 
            icon={<SettingOutlined />} 
            onClick={syncData}
          >
            同步数据
          </Button>
          <Button 
            danger
            icon={<StopOutlined />} 
            onClick={restartProxy}
            loading={restartLoading}
          >
            重启服务
          </Button>
        </Space>
      }>
        {/* 状态概览 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Alert
              message={
                <Space>
                  {getStatusIcon(status?.status)}
                  <span>Proxy服务状态: {getStatusBadge(status?.status)}</span>
                  {status?.timestamp && (
                    <span style={{ marginLeft: 16 }}>
                      最后更新: {dayjs(status.timestamp).format('YYYY-MM-DD HH:mm:ss')}
                    </span>
                  )}
                </Space>
              }
              type={status?.status === 'online' ? 'success' : 'error'}
              showIcon={false}
            />
          </Col>
        </Row>

        {/* 关键指标 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={6}>
            <Statistic
              title="服务状态"
              value={status?.status === 'online' ? '在线' : '离线'}
              valueStyle={{ 
                color: status?.status === 'online' ? '#52c41a' : '#ff4d4f' 
              }}
              prefix={getStatusIcon(status?.status)}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="运行时间"
              value={formatUptime(info?.uptime || status?.health?.uptime)}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="响应时间"
              value={status?.responseTime || '未知'}
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
          <Col span={6}>
            <Statistic
              title="成功率"
              value={`${getSuccessRate().toFixed(1)}%`}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
        </Row>

        {/* 详细统计 */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="总请求数"
                  value={stats.requestStats?.totalRequests || stats.totalRequests || 0}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="成功请求"
                  value={stats.requestStats?.successfulRequests || stats.successfulRequests || 0}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="失败请求"
                  value={stats.requestStats?.failedRequests || stats.failedRequests || 0}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col span={6}>
              <Card size="small">
                <Statistic
                  title="Token使用量"
                  value={stats.requestStats?.tokenUsage?.total || stats.tokenUsage?.total || 0}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* 成功率进度条 */}
        <Row gutter={16} style={{ marginBottom: 24 }}>
          <Col span={24}>
            <Card title="请求成功率" size="small">
              <Progress
                percent={getSuccessRate()}
                status={getSuccessRate() >= 95 ? 'success' : getSuccessRate() >= 80 ? 'normal' : 'exception'}
                strokeColor={{
                  '0%': '#108ee9',
                  '100%': '#87d068',
                }}
              />
            </Card>
          </Col>
        </Row>

        {/* 服务信息 */}
        <Row gutter={16}>
          <Col span={12}>
            <Card title="服务信息" size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label="版本">
                  {info?.version || status?.health?.version || '1.0.0'}
                </Descriptions.Item>
                <Descriptions.Item label="环境">
                  <Tag color={info?.environment === 'production' ? 'red' : 'blue'}>
                    {info?.environment || 'development'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="运行时间">
                  {formatUptime(info?.uptime || status?.health?.uptime)}
                </Descriptions.Item>
                <Descriptions.Item label="最后检查">
                  {status?.timestamp ? 
                    dayjs(status.timestamp).format('YYYY-MM-DD HH:mm:ss') : 
                    '未知'
                  }
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="健康检查" size="small">
              {status?.health ? (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="状态">
                    <Badge 
                      status={status.health.status === 'ok' ? 'success' : 'error'} 
                      text={status.health.status || '未知'} 
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="数据库">
                    <Badge
                      status={status.health.connectedToExtension !== false ? 'success' : 'error'}
                      text={status.health.connectedToExtension !== false ? '正常' : '异常'}
                    />
                  </Descriptions.Item>
                  <Descriptions.Item label="内存使用">
                    {status.health.memory ?
                      `${Math.round(status.health.memory.heapUsed / 1024 / 1024)}MB` :
                      '未知'
                    }
                  </Descriptions.Item>
                  <Descriptions.Item label="CPU使用">
                    {status.health.cpu ? `${status.health.cpu}%` : '未知'}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Alert message="健康检查数据不可用" type="warning" />
              )}
            </Card>
          </Col>
        </Row>
      </Card>
    </div>
  )
}

export default ProxyStatusPage
